import { Input, InputGroup, TextField } from '@heroui/react';
import { Palette } from 'lucide-react';
import { cn } from '@/lib/cn';
import { FieldLabel } from './FieldLabel';

const DEFAULT_FALLBACK = '#441249';

const IS_HEX = /^#[0-9a-fA-F]{6}$/;

interface ColorPickerProps {
  /** Текущее значение (#RRGGBB или пустая строка/null) */
  value: string;
  onChange?: (value: string) => void;
  label?: string;
  /** Цвет свотча, пока значение не задано/невалидно */
  fallback?: string;
  /** Просмотр: без пикера и поля ввода */
  readOnly?: boolean;
  /**
   * swatch — крупный квадрат-свотч над hex-полем («Оформление» клиента);
   * field — компактное поле «свотч + hex» в один ряд с другими полями
   * формы (стоимость/цвет шаблона)
   */
  variant?: 'swatch' | 'field';
}

/**
 * Пикер цвета. Свотч — скрытый native `<input type="color">` (системный
 * диалог). Вынесен из ClientEditPage — используется для primary_color
 * клиента и цвета шаблона визита.
 */
export function ColorPicker({
  value,
  onChange,
  label = 'Цвет',
  fallback = DEFAULT_FALLBACK,
  readOnly = false,
  variant = 'swatch',
}: ColorPickerProps) {
  const hasValue = IS_HEX.test(value);
  const colorValue = hasValue ? value : fallback;
  const handleChange = onChange ?? (() => {});

  if (variant === 'field') {
    return (
      <div className="flex flex-col gap-1.5">
        {/* Подпись текстом (дефолтный стиль HeroUI `.label`): настоящий
            Label здесь не с чем ассоциировать — hex-инпут подписан
            через aria-label */}
        <span className="text-sm font-medium text-foreground">{label}</span>
        {readOnly ? (
          // Просмотр: точка цвета + hex; без значения точка нейтральная
          <div className="flex items-center gap-2 text-sm text-foreground">
            <span
              className={cn(
                'size-4 shrink-0 rounded-full border',
                hasValue ? 'border-black/10' : 'border-border bg-surface-secondary',
              )}
              style={hasValue ? { backgroundColor: colorValue } : undefined}
            />
            <span className="font-mono">{value || '—'}</span>
          </div>
        ) : (
          <InputGroup variant="secondary" className="w-36">
            {/* Свотч-префикс: клик открывает системный пикер */}
            <InputGroup.Prefix>
              <label
                className="relative size-5 cursor-pointer rounded-[var(--radius-sm)] border border-black/10"
                style={{ backgroundColor: colorValue }}
                title="Выбрать цвет"
              >
                <input
                  type="color"
                  aria-label={label}
                  className="absolute inset-0 size-full cursor-pointer opacity-0"
                  value={colorValue}
                  onChange={(e) => handleChange(e.target.value.toUpperCase())}
                />
              </label>
            </InputGroup.Prefix>
            <InputGroup.Input
              value={value}
              onChange={(e) => handleChange(e.target.value.toUpperCase())}
              placeholder={fallback}
              aria-label={label}
              spellCheck={false}
              className="font-mono text-xs uppercase"
            />
          </InputGroup>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {readOnly ? (
        // Просмотр: тот же свотч, клик ничего не открывает
        <div
          className="flex size-16 items-center justify-center overflow-hidden rounded-[var(--radius)] border border-[var(--border)]"
          style={{ backgroundColor: colorValue }}
          title={colorValue}
        >
          <Palette className="size-5 text-white drop-shadow" aria-hidden="true" />
        </div>
      ) : (
        <label
          className="relative flex size-16 cursor-pointer items-center justify-center overflow-hidden rounded-[var(--radius)] border border-[var(--border)]"
          style={{ backgroundColor: colorValue }}
          title="Выбрать цвет"
        >
          <Palette className="size-5 text-white drop-shadow" aria-hidden="true" />
          <input
            type="color"
            aria-label={label}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
            value={colorValue}
            onChange={(e) => handleChange(e.target.value.toUpperCase())}
          />
        </label>
      )}
      {readOnly ? (
        <span className="font-mono text-xs text-muted">{value || '—'}</span>
      ) : (
        <TextField variant="secondary" value={value} onChange={handleChange} className="w-24">
          <FieldLabel>{label}</FieldLabel>
          <Input placeholder={fallback} />
        </TextField>
      )}
    </div>
  );
}
